import algoliasearch from 'algoliasearch/lite';
import instantsearch, { GeoLoc, UiState } from 'instantsearch.js';
import {
  connectAutocomplete,
  connectCurrentRefinements,
  connectGeoSearch,
  connectSearchBox,
  connectSortBy,
} from 'instantsearch.js/es/connectors';
import { history } from 'instantsearch.js/es/lib/routers';
import {
  queryRuleCustomData,
  hits,
  pagination,
  refinementList,
  stats,
  configure,
} from 'instantsearch.js/es/widgets';
import DOMPurify from 'isomorphic-dompurify';

import { autocompleteSearch } from '@/src/utilities/search/searchRenderFunctions';
import {
  cleanQueryString,
  deg2rad,
  getRoundedRadiusFromUrl,
  getZipcodeFromUrl,
  insertUrlZipcode,
  newYorkCities,
  removeUrlZipcode,
  RouteState,
  shortenZipcode,
  toggleDistanceAccordion,
  uniqBy,
} from '@/src/utilities/search/utils';

export function loadLocationSearch(): void {
  const config = window['location-search'];

  if (!config) {
    return;
  }

  const {
    applicationId,
    searchKey,
    searchContext,
    searchIndexName,
    suggestionsIndexName,
    placeholderImageURL,
  } = config;

  const { searchTerm: query } = searchContext;

  const algoliaClient = algoliasearch(applicationId, searchKey);

  const searchClient = {
    ...algoliaClient,
    search(requests: any[]) {
      return algoliaClient.search(requests);
    },
  };

  const search = instantsearch({
    indexName: `${searchIndexName.default}`,
    insights: true,
    searchClient,
    routing: {
      router: history({
        createURL({ qsModule, routeState, location }) {
          const urlParts = location.href.match(/^(.*?)\/search/);
          const baseUrl = `${urlParts ? urlParts[1] : ''}/`;

          type QueryParams = {
            term?: string;
            zipcode?: string;
            sortBy?: string;
            relatedConditions?: string[];
            locationTypes?: string[];
            distance?: string;
            relatedDepartments?: string[];
            page?: number;
          };

          const queryParameters = {} as QueryParams;
          const pageNumber: number = routeState['page'] as number;

          // set the query
          if (routeState['term']) {
            queryParameters.term = routeState['term'] as string;
          }

          // set the zipcode / city
          if (routeState['zipcode']) {
            queryParameters.zipcode = routeState['zipcode'] as string;
          }

          // set the sorting method
          if (routeState['sortBy'] === `${searchIndexName.distance}`) {
            queryParameters.sortBy = 'Distance';
          } else {
            queryParameters.sortBy = 'Alphabetical';
          }

          // RelatedConditions
          if (routeState['relatedConditions']) {
            queryParameters.relatedConditions = routeState[
              'relatedConditions'
            ] as string[];
          }

          // set the location type facets
          if (routeState['locationTypes']) {
            queryParameters.locationTypes = routeState[
              'locationTypes'
            ] as string[];
          }

          // set the distance
          if (routeState['distance']) {
            queryParameters.distance = routeState['distance'] as string;
          }

          // RelatedDepartments
          if (routeState['relatedDepartments']) {
            queryParameters.relatedDepartments = routeState[
              'relatedDepartments'
            ] as string[];
          }

          // set the page number
          if (pageNumber !== 1) {
            queryParameters.page = pageNumber;
          }

          const queryString = qsModule.stringify(queryParameters, {
            addQueryPrefix: true,
            arrayFormat: 'repeat',
          });

          return `${baseUrl}search${cleanQueryString(queryString)}`;
        },

        parseURL({ qsModule, location }): UiState {
          const {
            query = '',
            zipcode = '',
            sortBy = '',
            relatedConditions = [],
            locationTypes = [],
            distance = '',
            relatedDepartments = [],
            page,
          } = qsModule.parse(location.search.slice(1));
          // `qs` does not return an array when there's a single value.

          const indexName: string =
            sortBy === 'Distance'
              ? `${searchIndexName.distance}`
              : sortBy === 'Alphabetical'
              ? `${searchIndexName.alphabetical}`
              : `${searchIndexName.default}`;

          const allLocationTypes = Array.isArray(locationTypes)
            ? locationTypes
            : [locationTypes].filter(Boolean);

          const allRelatedConditions = Array.isArray(relatedConditions)
            ? relatedConditions
            : [relatedConditions].filter(Boolean);

          const allRelatedDepartments = Array.isArray(relatedDepartments)
            ? relatedDepartments
            : [relatedDepartments].filter(Boolean);

          return {
            term: decodeURIComponent(query as string),
            zipcode: zipcode as string,
            sortBy: indexName,
            locationTypes: allLocationTypes,
            relatedConditions: allRelatedConditions,
            relatedDepartments: allRelatedDepartments,
            distance: distance as string,
            page,
          };
        },
      }),

      stateMapping: {
        stateToRoute(uiState: UiState): RouteState {
          const indexUiState = uiState[searchIndexName.default];

          const geocodeInput = document.getElementById(
            'geocodeInput'
          ) as HTMLInputElement;

          const zipcode: string = geocodeInput.value;

          const distanceParam: number | 'all' | undefined = roundedRadius;

          return {
            term: indexUiState?.query,
            zipcode: zipcode, // this should be a zipcode, not a lat/lng
            distance: distanceParam,
            sortBy: indexUiState?.sortBy,
            locationTypes:
              indexUiState?.refinementList &&
              indexUiState?.refinementList['ContentType'],
            relatedConditions:
              indexUiState?.refinementList &&
              indexUiState?.refinementList['RelatedConditions'],
            relatedDepartments:
              indexUiState?.refinementList &&
              indexUiState?.refinementList['RelatedDepartments'],
            page: indexUiState?.page,
          };
        },
        routeToState(routeState: UiState): UiState {
          const selectedLocationTypes = Array.isArray(
            routeState['locationTypes']
          )
            ? routeState['locationTypes']
            : [routeState['locationTypes']];

          const selectedConditions = Array.isArray(
            routeState['relatedConditions']
          )
            ? routeState['relatedConditions']
            : [routeState['relatedConditions']];

          const selectedDepartments = Array.isArray(
            routeState['relatedDepartments']
          )
            ? routeState['relatedDepartments']
            : [routeState['relatedDepartments']];

          // sanitized versions
          const cleanLocationTypes = selectedLocationTypes.filter(
            (locationType) => {
              const isPlainText =
                typeof locationType === 'string' &&
                !/<[^>]*>/.test(locationType);
              const sanitized = DOMPurify.sanitize(locationType);

              // if sanitized value is plain text, return it
              return sanitized && isPlainText;
            }
          );

          const cleanConditions = selectedConditions.filter((condition) => {
            const isPlainText =
              typeof condition === 'string' && !/<[^>]*>/.test(condition);
            const sanitized = DOMPurify.sanitize(condition);

            // if sanitized value is plain text, return it
            return sanitized && isPlainText;
          });

          const cleanDepartments = selectedDepartments.filter((department) => {
            const isPlainText =
              typeof department === 'string' && !/<[^>]*>/.test(department);
            const sanitized = DOMPurify.sanitize(department);

            // if sanitized value is plain text, return it
            return sanitized && isPlainText;
          });

          return {
            [searchIndexName.default]: {
              query: routeState['term'],
              sortBy: routeState['sortBy'],
              refinementList: {
                ContentType: cleanLocationTypes,
                RelatedConditions: cleanConditions,
                RelatedDepartments: cleanDepartments,
              },
              page: routeState['page'],
            },
          };
        },
      },
    },
    searchFunction(helper) {
      const select = document.querySelector(
        '.ais-SortBy-select'
      ) as HTMLSelectElement;

      sortingMethod = select?.value.endsWith('distance')
        ? 'DISTANCE'
        : select?.value.endsWith('alphabetical')
        ? 'LOCATION NAME'
        : 'ERROR';

      helper.search();
    },
  });

  const initialLocationTypes: any[] = [];

  let userLocation: { lat: number; lng: number } | null = null;

  let roundedRadius: number | 'all' | undefined = getRoundedRadiusFromUrl();

  let sortingMethod: null | undefined | string = null;

  const geocoder = new google.maps.Geocoder();

  window.addEventListener('load', () => {
    const geocodeInput = document.getElementById(
      'geocodeInput'
    ) as HTMLInputElement;
    geocodeInput.value = getZipcodeFromUrl();

    const sortByDropdown = document.querySelector('.ais-SortBy-select');
    sortByDropdown?.setAttribute('id', 'sortBy');

    const zipcode = getZipcodeFromUrl();
    if (zipcode.length > 0) {
      geocode(zipcode);
    }
    toggleDistanceAccordion();
  });

  // render functions:
  const renderAutocomplete = (
    renderOptions: {
      indices: object[];
      currentRefinement: string;
      refine: any; // should be a function
      widgetParams: any; // should be an object
    },
    isFirstRender: boolean
  ): void => {
    const { refine, widgetParams } = renderOptions;

    if (isFirstRender) {
      const aaForm = document.createElement('form'); // aa-Form

      const aaPanel = document.createElement('div'); // dropdown panel

      const suggestionsList = document.createElement('div'); // contains 1 li per hit/suggestion
      const resultsList = document.createElement('div'); // contains 1 li per hit/suggestion
      suggestionsList.classList.add('suggestions-list');
      resultsList.classList.add('results-list');
      aaPanel.appendChild(suggestionsList);
      aaPanel.appendChild(resultsList);

      const aaInputWrapperPrefix = document.createElement('div'); // aa-InputWrapperPrefix
      const aaLabel = document.createElement('label'); // aa-Label

      const aaExtendedSearchbox = document.createElement('div'); // aa-ExtendedSearchbox
      const aaClearButton = document.createElement('button'); // aa-ClearButton
      aaClearButton.setAttribute('aria-label', 'Clear search form');

      const aaSubmitButton = document.createElement(
        'button'
      ) as HTMLButtonElement; // aa-SubmitButton

      const aaLoadingIndicator = document.createElement('div'); // aa-LoadingIndicator
      const aaLoadingIcon = document.createElement('svg'); // aa-LoadingIcon

      const aaInputWrapper = document.createElement('div'); // aa-InputWrapper
      const aaInput = document.createElement('input'); // aa-Input

      const aaExtendedGeobox = document.createElement('div');
      const geoClearButton = document.createElement('button');
      geoClearButton.setAttribute('aria-label', 'Clear city or zip code input');

      const geocodeLabel = document.createElement('label'); // geocode label
      const geocodeInput = document.createElement('input'); // geocode input
      const currentLocationContainer = document.createElement('div');
      const iconContainer = document.createElement('div');
      const currentLocationButton = document.createElement('button'); // current location button

      const aaInputWrapperSuffix = document.createElement('div'); // aa-InputWrapperSuffix

      aaInput.value = query;

      const url = new URL(window.location.href);
      const searchParams = url.searchParams;

      const zipcode = searchParams.get('zipcode');

      geocodeInput.value = zipcode ?? '';

      autocompleteSearch(query);

      aaInput.addEventListener('focus', (event) => {
        const eventTarget = event.target as HTMLInputElement;

        if (eventTarget.value.length > 0) {
          aaClearButton.classList.remove('hidden');
        }

        if (eventTarget.value.length > 1) {
          aaPanel.classList.remove('hidden');
        }
      });

      aaInput.addEventListener('input', (event) => {
        const eventTarget = event.target as HTMLInputElement;
        if (eventTarget.value.length > 1) {
          aaPanel.classList.remove('hidden');
        } else {
          aaPanel.classList.add('hidden');
        }

        if (aaInput.value.length > 0) {
          aaClearButton.classList.remove('hidden');
        } else {
          aaClearButton.classList.add('hidden');
        }

        autocompleteSearch(eventTarget.value);
      });

      let panelIsFocused = false;
      aaPanel.addEventListener('mousedown', (event) => {
        event.preventDefault();

        panelIsFocused = true;
      });

      aaClearButton.addEventListener('mousedown', (event) => {
        event.preventDefault();
        aaPanel.classList.add('hidden');
      });

      aaInput.addEventListener('blur', () => {
        searchContext.searchTerm = aaInput.value;
        autocompleteSearch(aaInput.value);

        if (!panelIsFocused) {
          aaPanel.classList.add('hidden');
        }
      });

      // add hover state on mouse enter
      // Add a single event listener to the parent element
      aaPanel.addEventListener(
        'mouseenter',
        (event) => {
          const target = event.target as HTMLElement;

          if (target.tagName === 'A') {
            target.classList.add('bg-stone-100', 'hover:underline');
          }
        },
        true
      ); // Use capture phase to ensure it catches the event

      // remove background color on mouse leave UNLESS it's the currently focused element
      // Add a single event listener to the parent element
      aaPanel.addEventListener(
        'mouseleave',
        (event) => {
          const target = event.target as HTMLElement;
          const currentFocus = document.querySelector('#selected-query');

          if (target.tagName === 'A') {
            if (target !== currentFocus) {
              target.classList.remove('bg-stone-100');
            }
          }
        },
        true
      ); // Use capture phase to ensure it catches the event

      // arrow key navigation
      aaInput.addEventListener('keydown', (event: KeyboardEvent) => {
        const focusableEls = Array.prototype.slice.call(
          aaPanel.querySelectorAll('a')
        );
        const lastFocusableEl = focusableEls[focusableEls.length - 1];

        const currentFocus = document.querySelector('#selected-query');
        let currentIndex = focusableEls.indexOf(currentFocus);
        const nextFocusableEl = focusableEls[currentIndex + 1];
        const prevFocusableEl = focusableEls[currentIndex - 1];

        if (event.key === 'ArrowDown' && aaInput.value.length > 1) {
          event.preventDefault();

          if (currentIndex === -1) {
            searchContext.searchTerm = aaInput.value;
          }

          aaPanel.classList.remove('hidden');

          currentFocus?.removeAttribute('id');

          // background color is not removed if the element is currently hovered
          if (!currentFocus?.matches(':hover')) {
            currentFocus?.classList.remove('bg-stone-100');
          }

          currentIndex++;

          if (!nextFocusableEl) {
            currentFocus?.removeAttribute('id');
            currentFocus?.classList.remove('bg-stone-100');

            aaPanel.scroll({ top: 0, behavior: 'smooth' });

            aaInput.value = searchContext.searchTerm;
          } else {
            nextFocusableEl.classList.add('bg-stone-100');

            nextFocusableEl.setAttribute('id', 'selected-query');

            nextFocusableEl.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'nearest',
            });

            console.log(nextFocusableEl.textContent);
            aaInput.value = nextFocusableEl.textContent.trim();
          }
        }

        if (event.key === 'ArrowUp' && aaInput.value.length > 1) {
          event.preventDefault();

          if (currentIndex === -1) {
            searchContext.searchTerm = aaInput.value;
          }

          aaPanel.classList.remove('hidden');

          currentFocus?.removeAttribute('id');

          // background color is not removed if the element is currently hovered
          if (!currentFocus?.matches(':hover')) {
            currentFocus?.classList.remove('bg-stone-100');
          }

          currentIndex--;

          if (!prevFocusableEl) {
            currentFocus?.removeAttribute('id');
            currentFocus?.classList.remove('bg-stone-100');

            if (!currentFocus) {
              lastFocusableEl.classList.add('bg-stone-100');

              lastFocusableEl.setAttribute('id', 'selected-query');
              lastFocusableEl.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest',
              });

              aaInput.value = lastFocusableEl.textContent.trim();
            } else {
              aaInput.value = searchContext.searchTerm;
            }
          } else {
            prevFocusableEl.classList.add('bg-stone-100');

            prevFocusableEl.setAttribute('id', 'selected-query');
            prevFocusableEl.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'nearest',
            });

            aaInput.value = prevFocusableEl.textContent.trim();
          }
        }

        if (event.key === 'Enter') {
          event.preventDefault();

          if (currentFocus?.hasAttribute('data-app-autocomplete-result')) {
            const url = currentFocus.getAttribute('href') as string;
            window.location.href = url;
          } else {
            if (currentFocus) {
              const selectedQuery = currentFocus.textContent?.trim() as string;
              aaInput.value = selectedQuery;
            }
            refine(aaInput.value);
            autocompleteSearch(aaInput.value);
            aaPanel.classList.add('hidden');
          }
        }
      });

      aaClearButton.addEventListener('click', (event) => {
        event.preventDefault();
        aaInput.value = '';
        aaClearButton.classList.add('hidden');
      });

      geoClearButton.addEventListener('click', (event) => {
        event.preventDefault();
        geocodeInput.value = '';
        geoClearButton.classList.add('hidden');
      });

      geocodeInput.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          event.preventDefault();

          const query = aaInput.value;
          const zipcode = geocodeInput.value;

          toggleDistanceAccordion();
          if (zipcode.length > 0) {
            geocode(zipcode);
            insertUrlZipcode();
          }

          if (query.length > 0) {
            refine(query);
          }
        }
      });

      geocodeInput.addEventListener('input', () => {
        if (geocodeInput.value.length > 0) {
          geoClearButton.classList.remove('hidden');
        } else {
          geoClearButton.classList.add('hidden');
        }
      });

      // autocomplete panel section
      // aaPanel, ul
      aaPanel.setAttribute(
        'class',
        'aa-Panel search-autocomplete-panel z-50 w-full hidden'
      );

      aaForm.setAttribute('class', 'lg:flex gap-4 items-start');

      aaLoadingIndicator.setAttribute('class', 'aa-LoadingIndicator');
      aaLoadingIcon.setAttribute('class', 'aa-LoadingIcon');

      // prefix section
      // aaInput, aaLabel, aaClearButton
      aaExtendedSearchbox.setAttribute(
        'class',
        'aa-ExtendedSearchbox flex items-center relative'
      );

      const toggleQueryClearButton = aaInput.value.length > 0 ? '' : 'hidden';

      aaClearButton.setAttribute(
        'class',
        `-ml-7 ${toggleQueryClearButton} group absolute right-3`
      );

      aaClearButton.innerHTML = `
        <svg width="16" height="15" viewBox="0 0 16 15" class="w-4 h-4" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path class="fill-stone-400 group-hover:fill-current" d="M0.304688 7.5C0.304688 3.35742 3.66211 0 7.80469 0C11.9473 0 15.3047 3.35742 15.3047 7.5C15.3047 11.6426 11.9473 15 7.80469 15C3.66211 15 0.304688 11.6426 0.304688 7.5ZM5.43164 6.09668L6.81152 7.47363L5.43164 8.87695C5.15918 9.15234 5.15918 9.59766 5.43164 9.84668C5.70703 10.1455 6.15234 10.1455 6.40137 9.84668L7.77832 8.49316L9.18164 9.84668C9.45703 10.1455 9.90234 10.1455 10.1514 9.84668C10.4502 9.59766 10.4502 9.15234 10.1514 8.87695L8.79785 7.47363L10.1514 6.09668C10.4502 5.84766 10.4502 5.40234 10.1514 5.12695C9.90234 4.85449 9.45703 4.85449 9.18164 5.12695L7.77832 6.50684L6.40137 5.12695C6.15234 4.85449 5.70703 4.85449 5.43164 5.12695C5.15918 5.40234 5.15918 5.84766 5.43164 6.09668Z"/>
        </svg>`;

      aaInputWrapperPrefix.setAttribute(
        'class',
        'aaInputWrapperPrefix flex-1 relative'
      );

      aaLabel.setAttribute('for', 'urmcQuery');
      aaLabel.setAttribute('class', 'mb-3 font-semibold inline-block');
      aaLabel.innerHTML = `Location Name or Keyword <span class="font-normal text-gray-700 italic block sm:ml-3 sm:inline-block">For example, Strong Memorial Hospital, Primary Care…</span>`;

      aaInput.setAttribute(
        'class',
        'border rounded w-full p-3 leading-snug border-stone-400'
      );
      aaInput.setAttribute('id', 'urmcQuery');
      aaInput.setAttribute('name', 'urmcQuery');

      aaInput.setAttribute('type', 'text');
      aaInput.setAttribute('autocomplete', 'off');

      // wrapper section
      // geocodeInput, geocodeLabel, currentLocationButton, geoclearbutton
      aaExtendedGeobox.setAttribute(
        'class',
        'aa-ExtendedGeobox flex items-center relative'
      );

      const toggleGeoClearButton =
        geocodeInput.value.length > 0 ? '' : 'hidden';

      geoClearButton.setAttribute(
        'class',
        `-ml-7 ${toggleGeoClearButton} group absolute right-3`
      );

      geoClearButton.innerHTML = `
        <svg width="16" height="15" viewBox="0 0 16 15" class="w-4 h-4" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path class="fill-stone-400 group-hover:fill-current" d="M0.304688 7.5C0.304688 3.35742 3.66211 0 7.80469 0C11.9473 0 15.3047 3.35742 15.3047 7.5C15.3047 11.6426 11.9473 15 7.80469 15C3.66211 15 0.304688 11.6426 0.304688 7.5ZM5.43164 6.09668L6.81152 7.47363L5.43164 8.87695C5.15918 9.15234 5.15918 9.59766 5.43164 9.84668C5.70703 10.1455 6.15234 10.1455 6.40137 9.84668L7.77832 8.49316L9.18164 9.84668C9.45703 10.1455 9.90234 10.1455 10.1514 9.84668C10.4502 9.59766 10.4502 9.15234 10.1514 8.87695L8.79785 7.47363L10.1514 6.09668C10.4502 5.84766 10.4502 5.40234 10.1514 5.12695C9.90234 4.85449 9.45703 4.85449 9.18164 5.12695L7.77832 6.50684L6.40137 5.12695C6.15234 4.85449 5.70703 4.85449 5.43164 5.12695C5.15918 5.40234 5.15918 5.84766 5.43164 6.09668Z"/>
        </svg>`;

      geocodeInput.setAttribute(
        'class',
        'w-full rounded border border-stone-400 p-3 leading-4'
      );
      geocodeInput.setAttribute('id', 'geocodeInput');
      geocodeInput.setAttribute('type', 'text');
      geocodeInput.setAttribute('autocomplete', 'off');

      geocodeLabel.setAttribute('for', 'geocodeInput');
      geocodeLabel.setAttribute(
        'class',
        'mb-3 mt-6 font-semibold block lg:mt-0'
      );
      geocodeLabel.innerHTML = 'City or Zip Code';
      currentLocationButton.setAttribute('type', 'button');
      currentLocationButton.setAttribute('class', 'group-hover:underline');
      currentLocationButton.innerHTML = 'Use current location';

      iconContainer.setAttribute('class', 'w-3.5 h-3.5 mr-2');
      iconContainer.innerHTML = `
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            class="w-full h-full"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              class="fill-current"
              d="M7.34516 11.4582C7.18715 11.8451 6.77848 12.0631 6.36982 11.9841C5.96115 11.9023 5.6424 11.5454 5.6424 11.1286V6.3336H0.84741C0.455365 6.3336 0.0968307 6.03936 0.0164601 5.63069C-0.0639104 5.22203 0.156223 4.81337 0.541729 4.65535L10.1322 0.731634C10.4565 0.598682 10.8297 0.673876 11.0776 0.922071C11.3255 1.17054 11.4018 1.54378 11.2683 1.86881L7.34516 11.4582Z"
            />
          </svg>`;
      currentLocationContainer.setAttribute(
        'class',
        'flex items-center text-pacific-blue group hover:text-navy-blue cursor-pointer mt-2'
      );

      // suffix section
      // aaSubmitButton
      const spacer = document.createElement('div');
      spacer.setAttribute('class', 'search-btn-spacer');
      aaSubmitButton.setAttribute(
        'class',
        'btn btn-primary my-4 w-full max-w-none lg:my-0'
      );
      aaSubmitButton.innerHTML = 'Search';

      aaForm.onsubmit = (event) => {
        event.preventDefault();
        refine(aaInput.value);

        if (geocodeInput.value.length > 0) {
          geocode(geocodeInput.value);
          toggleDistanceAccordion();
          insertUrlZipcode();
        } else {
          userLocation = null;
          toggleDistanceAccordion();
          removeUrlZipcode();
        }
      };

      currentLocationButton.addEventListener('click', (e) => {
        e.preventDefault();
        getUserLatLng();
        searchContext.geoSearchTerm =
          geocodeInput.value.length > 0 ? geocodeInput.value : '';
        geoClearButton.classList.remove('hidden');
      });

      if (
        typeof widgetParams.container === 'object' &&
        widgetParams.container !== null &&
        'appendChild' in widgetParams.container
      ) {
        widgetParams.container.appendChild(aaForm);
        widgetParams.container.appendChild(aaLoadingIndicator);

        // aaForm section
        aaForm.appendChild(aaInputWrapperPrefix);
        aaForm.appendChild(aaInputWrapper);
        aaForm.appendChild(aaInputWrapperSuffix);

        // aaExtendedSearchbox
        aaExtendedSearchbox.appendChild(aaInput);
        aaExtendedSearchbox.appendChild(aaClearButton);

        // prefix
        aaInputWrapperPrefix.appendChild(aaLabel);
        aaInputWrapperPrefix.appendChild(aaExtendedSearchbox);
        aaInputWrapperPrefix.appendChild(aaPanel);

        // aaExtendedGeobox
        aaExtendedGeobox.appendChild(geocodeInput);
        aaExtendedGeobox.appendChild(geoClearButton);

        // currentLocationContainer
        currentLocationContainer.appendChild(iconContainer);
        currentLocationContainer.appendChild(currentLocationButton);

        // wrapper
        aaInputWrapper.appendChild(geocodeLabel);
        aaInputWrapper.appendChild(aaExtendedGeobox);
        aaInputWrapper.appendChild(currentLocationContainer);

        // suffix
        aaInputWrapperSuffix.appendChild(spacer);
        aaInputWrapperSuffix.appendChild(aaSubmitButton);
      }

      refine(aaInput.value);

      // end of isFirstRender conditional
    }
  };

  const createDataAttributes = (refinement: any) =>
    Object.keys(refinement)
      .map((key) => `data-${key}="${refinement[key]}"`)
      .join(' ');

  const renderListItem = (item: any) =>
    `
    ${item.refinements
      .map(
        (refinement: any) =>
          `<li class="ais-CurrentRefinements-category filter-pill">
            ${
              refinement.attribute === 'IsAcceptingNewPatients' &&
              refinement.value === 'true'
                ? 'Accepting New Patients'
                : refinement.attribute === 'IsAllowOpenScheduling' &&
                  refinement.value === 'true'
                ? 'Has Online Scheduling'
                : refinement.attribute === 'Gender' && refinement.value === 'M'
                ? 'Male'
                : refinement.attribute === 'Gender' && refinement.value === 'F'
                ? 'Female'
                : refinement.label
            }
            <button class="text-pacific-blue ml-2" ${createDataAttributes(
              refinement
            )}>✕</button>
          </li>`
      )
      .join('')}
    `;

  const renderCurrentRefinements = (renderOptions: {
    items: any;
    refine: any;
    widgetParams: any;
  }) => {
    const { items, refine, widgetParams } = renderOptions;

    const container = widgetParams.container;

    if (
      typeof container === 'object' &&
      container !== null &&
      'appendChild' in container
    ) {
      container.innerHTML = `
            <ul class="filter-list list-none">
              ${items.map(renderListItem).join('')}
            </ul>
          `;

      [...container.querySelectorAll('button')].forEach((element) => {
        element.addEventListener(
          'click',
          (event: { currentTarget: { dataset: { [x: string]: any } } }) => {
            const item = Object.keys(event.currentTarget.dataset).reduce(
              (acc, key) => ({
                ...acc,
                [key]: event.currentTarget.dataset[key],
              }),
              {}
            );

            refine(item);
          }
        );

        const disabledCheckboxes = document.querySelectorAll(
          '[disabled-checkbox]'
        );

        disabledCheckboxes.forEach((checkbox) => {
          if (element.attributes['data-label'].value === checkbox.value) {
            element.click();
          }
        });
      });
    }
  };

  const customCurrentRefinements = connectCurrentRefinements(
    renderCurrentRefinements
  );

  const renderGeoSearch = (
    renderOptions: {
      items: any;
      refine: any;
      widgetParams: any;
    },
    isFirstRendering: any
  ) => {
    const { widgetParams } = renderOptions;

    const { container } = widgetParams;

    if (isFirstRendering) {
      const ul = document.createElement('ul');

      ul.classList.add('list-none', 'space-y-4');

      if (
        typeof container === 'object' &&
        container !== null &&
        'appendChild' in container
      ) {
        container.appendChild(ul);
      }
    }

    if (
      typeof container === 'object' &&
      container !== null &&
      'appendChild' in container
    ) {
      container.querySelector('ul').innerHTML = `
        <li>
          <input
            type="radio"
            id="distance-all"
            class="mr-2.5 ml-1 my-1"
            name="distance"
            value="any distance"
            data-value="all"
            ${roundedRadius === 'all' ? 'checked' : ''}
            >
          <label for="distance-all">Any Distance</label>
        </li>
        <li>
          <input
            type="radio"
            id="distance-5"
            class="mr-2.5 ml-1 my-1"
            name="distance"
            value="5 miles"
            data-value="5"
            ${roundedRadius === Math.round(1609.34 * 5) ? 'checked' : ''}
            >
          <label for="distance-5">Within 5 miles</label>
        </li>
        <li>
          <input
            type="radio"
            id="distance-10"
            class="mr-2.5 ml-1 my-1"
            name="distance"
            value="10 miles"
            data-value="10"
            ${roundedRadius === Math.round(16093.4) ? 'checked' : ''}
            >
          <label for="distance-10">Within 10 miles</label>
        </li>
        <li>
          <input
            type="radio"
            id="distance-15"
            class="mr-2.5 ml-1 my-1"
            name="distance"
            value="15 miles"
            data-value="15"
            ${roundedRadius === Math.round(1609.34 * 15) ? 'checked' : ''}
            >
          <label for="distance-15">Within 15 miles</label>
        </li>
        <li>
          <input
            type="radio"
            id="distance-25"
            class="mr-2.5 ml-1 my-1"
            name="distance"
            value="25 miles"
            data-value="25"
            ${roundedRadius === Math.round(1609.34 * 25) ? 'checked' : ''}
            >
          <label for="distance-25">Within 25 miles</label>
        </li>
        <li>
          <input
            type="radio"
            id="distance-50"
            class="mr-2.5 ml-1 my-1"
            name="distance"
            value="50 miles"
            data-value="50"
            ${roundedRadius === Math.round(16093.4 * 5) ? 'checked' : ''}
            >
          <label for="distance-50">Within 50 miles</label>
        </li>
        <li>
          <input
            type="radio"
            id="distance-100"
            class="mr-2.5 ml-1 my-1"
            name="distance"
            value="100 miles"
            data-value="100"
            ${roundedRadius === 160934 ? 'checked' : ''}
          >
          <label for="distance-100">Within 100 miles</label>
        </li>
        `;
      [...container.querySelectorAll('input')].forEach((element) => {
        element.addEventListener(
          'click',
          (event: { currentTarget: { dataset: { value: any } } }) => {
            element.checked = true;
            if (userLocation) {
              updateRadius(event.currentTarget.dataset.value);

              const pageLinkArray = Array.prototype.slice.call(
                document.querySelectorAll('.ais-Pagination-link')
              );

              const firstPageButton = pageLinkArray[0];

              firstPageButton.click();
            }
          }
        );
      });
    }
  };

  const renderSortBy = (
    renderOptions: {
      options: any;
      currentRefinement: any;
      refine: any;
      widgetParams: any;
      canRefine: any;
    },
    isFirstRender: any
  ) => {
    const { options, currentRefinement, refine, widgetParams, canRefine } =
      renderOptions;

    if (
      typeof widgetParams.container === 'object' &&
      widgetParams.container !== null &&
      'appendChild' in widgetParams.container
    ) {
      if (isFirstRender) {
        const select = document.createElement('select');
        select.classList.add('ais-SortBy-select');

        select.addEventListener('change', () => {
          refine(select.value);

          refreshStats();
        });

        widgetParams.container.appendChild(select);
      }

      const select = widgetParams.container.querySelector('select');

      select.disabled = !canRefine;

      select.innerHTML = `
      ${options
        .map(
          (option: { value: any; label: any }) => `
          <option
          value="${option.value}"
          ${option.value === currentRefinement ? 'selected' : ''}
          >
          ${option.label}
          </option>
          `
        )
        .join('')}
          `;
    }
  };

  // Create the custom widget
  const customSortBy = connectSortBy(renderSortBy);
  // utils:

  // Add hover state to location name if image is hovered
  function imageHoverOn(objectID: string) {
    const profileLink = document.querySelector(`#link-${objectID}`);
    const profileimage = document.querySelector(`#img-${objectID}`);
    profileimage?.classList.add('hover');
    profileLink?.classList.add('text-navy-blue');
  }

  function imageHoverOff(objectID: string) {
    const profileLink = document.querySelector(`#link-${objectID}`);
    const profileimage = document.querySelector(`#img-${objectID}`);
    profileimage?.classList.remove('hover');
    profileLink?.classList.remove('text-navy-blue');
  }

  function getDistance(locationLat: number, locationLng: number) {
    const R = 3958.8; // Radius of the earth in miles

    const userLat = userLocation?.lat as number;
    const userLng = userLocation?.lng as number;

    const locationLatDeg = locationLat || 0;
    const locationLngDeg = locationLng || 0;

    const dLat = deg2rad(userLat - locationLatDeg);
    const dLng = deg2rad(userLng - locationLngDeg);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(deg2rad(userLat)) *
        Math.cos(deg2rad(locationLat)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c; // Distance in miles

    return d;
  }

  function refreshHits() {
    search.removeWidgets([
      hits({
        container: '[data-app-search-hits]',
      }),
    ]);
    search.addWidgets([
      configure({
        aroundLatLng: `${userLocation?.lat},${userLocation?.lng}`,
        aroundRadius: roundedRadius,
      }),
    ]);
  }

  function refreshStats() {
    search.removeWidgets([
      stats({
        container: '[data-app-search-stats]',
      }),
    ]);
    search.addWidgets([
      stats({
        container: '[data-app-search-stats]',
        cssClasses: {
          text: 'stats-text',
        },
        templates: {
          text(data, { html }) {
            let count = '';

            const sortByContainer =
              document.querySelector('.sort-by-container');

            if (data.hasNoResults) {
              sortByContainer?.classList.add('hidden');
            } else {
              sortByContainer?.classList.remove('hidden');
            }

            if (search.status === 'error') {
              return html`
                <div>
                  <p class="text-body-regular">Search is offline</p>
                  <p>
                    Sorry, it appears our search tool is currently offline or
                    being worked on. The issue should be resolved soon.
                  </p>
                </div>
              `;
            } else if (data.hasManyResults) {
              count += `${data.nbHits} Providers`;
            } else if (data.hasOneResult) {
              count += `1 Provider`;
            } else {
              count += `Sorry, we couldn't find any results`;
              return html`
                <p class="text-body-regular">
                  <strong>${count} for: "${data.query}"</strong>
                </p>
                <p class="text-body-lg mt-10">
                  Try rephrasing your search, or try browsing our
                  <span> </span>
                  <a href="/conditions-and-treatments">condition categories</a>
                  <span> </span>
                  or
                  <span> </span>
                  <a href="/people">primary care types</a>
                  <span> </span>
                  to find what you need.
                </p>
              `;
            }

            if (data.query) {
              return html` <p>
                <strong>${count}</strong> for
                <strong> "${data.query}"</strong> sorted by
                <strong> ${sortingMethod}</strong>
              </p>`;
            } else {
              return html` <p>
                <strong>${count}</strong> sorted by
                <strong> ${sortingMethod}</strong>
              </p>`;
            }
          },
        },
      }),
    ]);
  }

  // Function to geocode an address
  function geocode(userAddress: string) {
    const modifiedUserAddress = newYorkCities.includes(
      userAddress.toLowerCase()
    )
      ? `${userAddress}, NY`
      : shortenZipcode(userAddress);
    geocoder
      .geocode({
        address: modifiedUserAddress,
      })
      .then((r) => {
        if (r.results[0]?.geometry.location) {
          const lat = r.results[0]?.geometry.location.lat();
          const lng = r.results[0]?.geometry.location.lng();

          updateLatLng(lat, lng);
          refreshHits();

          //  programmatically update the index
          search.helper?.setIndex(`${searchIndexName.distance}`).search();

          //  programmatically update the sortBy widget's select element
          const select = document.querySelector(
            '.ais-SortBy-select'
          ) as HTMLSelectElement;
          select.value = `${searchIndexName.distance}`;
          // refresh the search to update the sortingMethod in the stats widget
          search.refresh();

          const sortedByInStatsWidget = document
            .querySelector('[data-app-search-stats]')
            ?.querySelector('p')?.lastElementChild?.innerHTML;
          if (sortingMethod !== sortedByInStatsWidget) {
            refreshStats();
          }
        } else {
          console.log('no data');
        }
      })
      .catch((e) => {
        console.log('error message', e.response.data.error_message);
      });
  }

  // function to turn lat/lng into a zipcode
  function reverseGeocode() {
    geocoder
      .geocode({
        location: userLocation,
      })
      .then((response) => {
        const result = response.results[0];
        const addressComponentsArray = result?.address_components;

        const zipComponent = addressComponentsArray?.find((component) =>
          component.types.includes('postal_code')
        );
        const zipcode = zipComponent?.short_name as string;

        if (zipcode) {
          const geocodeInput = document.querySelector(
            '#geocodeInput'
          ) as HTMLInputElement;

          geocodeInput.value = zipcode;
          geocodeInput.focus();
        } else {
          window.alert('No results found');
        }
      })
      .catch((e) => window.alert('Geocoder failed due to: ' + e));
  }

  function updateRadius(selectedRadiusInMiles: string) {
    const selectedRadiusInMeters =
      parseInt(selectedRadiusInMiles, 10) * 1609.34; // Convert miles to meters

    roundedRadius =
      selectedRadiusInMiles === 'all'
        ? 'all'
        : Math.round(selectedRadiusInMeters);

    search.addWidgets([
      configure({
        aroundLatLng: `${userLocation?.lat},${userLocation?.lng}`,
        aroundRadius: roundedRadius,
      }),
    ]);
  }

  function updateLatLng(lat: number, lng: number) {
    userLocation = { lat, lng };
  }
  function getUserLatLng() {
    const options = {
      enableHighAccuracy: true,
      timeout: 5000,
      maximumAge: 0,
    };

    function success(pos: { coords: any }) {
      const crd = pos.coords;

      userLocation = {
        lat: crd.latitude,
        lng: crd.longitude,
      };
      reverseGeocode();
    }

    function error(err: { code: number; message: string }) {
      console.warn(`ERROR(${err.code}): ${err.message}`);
    }

    navigator.geolocation.getCurrentPosition(success, error, options);
  }

  const virtualSearchBox = connectSearchBox(() => null);
  const customAutocomplete = connectAutocomplete(renderAutocomplete);
  const customGeoSearch = connectGeoSearch(renderGeoSearch);

  searchClient
    .searchForFacetValues([
      {
        indexName: `${searchIndexName.default}`,
        params: {
          facetName: 'ContentType',
          facetQuery: '',
          maxFacetHits: 100,
          filters: 'ClassName:"URMC.LocationPage"',
        },
      },
    ])
    .then(([{ facetHits }]) => {
      // I guess we'll ignore this error, seeing as it's wrong
      initialLocationTypes.push(
        ...facetHits.map((facet: { value: any }) => ({
          ...facet,
          label: facet.value,
          value: facet.value,
          isrefined: false,
          count: 0,
        }))
      );
    });

  search.addWidgets([
    configure({
      filters: 'ClassName:"URMC.LocationPage"',
    }),
    queryRuleCustomData({
      container: document.querySelector('[data-app-search-box]'),
      templates: {
        default: '',
      },
      transformItems(items) {
        const match = items.find((data) => Boolean(data.redirect));
        if (match && match.redirect) {
          window.location.href = match.redirect;
        }
        return [];
      },
    }),
    virtualSearchBox({}),
    customAutocomplete({
      container: document.querySelector('[data-app-search-box]'),
    }),

    hits({
      container: '[data-app-search-hits]',
      cssClasses: {
        list: 'list-none space-y-6',
        item: 'result-card',
      },
      templates: {
        item(hit, { html, components }) {
          const geolocArray: GeoLoc[] = hit['_geoloc'] as unknown as GeoLoc[];

          const distance =
            geolocArray[0] === undefined
              ? 'unknown'
              : Math.round(getDistance(geolocArray[0].lat, geolocArray[0].lng));

          const showDistance =
            userLocation === null ? 'hidden' : 'distance-tab';

          const distanceTab = html`<div class="${showDistance}">
            <div class="flex items-center">
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                class="w-3 h-3 mr-2.5"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  class="fill-current"
                  d="M7.34516 11.4582C7.18715 11.8451 6.77848 12.0631 6.36982 11.9841C5.96115 11.9023 5.6424 11.5454 5.6424 11.1286V6.3336H0.84741C0.455365 6.3336 0.0968307 6.03936 0.0164601 5.63069C-0.0639104 5.22203 0.156223 4.81337 0.541729 4.65535L10.1322 0.731634C10.4565 0.598682 10.8297 0.673876 11.0776 0.922071C11.3255 1.17054 11.4018 1.54378 11.2683 1.86881L7.34516 11.4582Z"
                />
              </svg>
              <p>${distance} miles away</p>
            </div>
          </div>`;
          const locationImage =
            hit['PrimaryImage'] === '' || !hit['PrimaryImage']
              ? placeholderImageURL
              : hit['PrimaryImage'];

          const parentHospitalDiv =
            hit['ParentHospitalTitle']?.length > 0
              ? html`
                  <div>
                    <span class="text-base">
                      Part of ${hit['ParentHospitalTitle']}
                    </span>
                    <button
                      class="relative group cursor-pointer"
                      aria-label="What is this?"
                    >
                      <span class="ml-1 tooltip">
                        <svg
                          class="w-4 h-4 fill-current"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 512 512"
                        >
                          <path
                            d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256s256-114.6 256-256S397.4 0 256 0zM256 464c-114.7 0-208-93.31-208-208S141.3 48 256 48s208 93.31 208 208S370.7 464 256 464zM256 336c-18 0-32 14-32 32s13.1 32 32 32c17.1 0 32-14 32-32S273.1 336 256 336zM289.1 128h-51.1C199 128 168 159 168 198c0 13 11 24 24 24s24-11 24-24C216 186 225.1 176 237.1 176h51.1C301.1 176 312 186 312 198c0 8-4 14.1-11 18.1L244 251C236 256 232 264 232 272V288c0 13 11 24 24 24S280 301 280 288V286l45.1-28c21-13 34-36 34-60C360 159 329 128 289.1 128z"
                          />
                        </svg>
                      </span>
                      <span role="tooltip" class="tooltip-modal">
                        This location is an outpatient hospital department of
                        <span> ${hit['ParentHospitalTitle']}</span>. There may
                        be additional insurance or private pay charges for
                        services provided here.
                      </span>
                    </button>
                  </div>
                `
              : null;
          const buildingName =
            hit['Address']?.BuildingName === ''
              ? ''
              : html`<span>${hit['Address']?.BuildingName}<br /></span>`;
          const lineTwo =
            hit['Address']?.Line2 === ''
              ? ''
              : html`<span>${hit['Address']?.Line2}<br /></span>`;

          const phone = hit['PhoneNumbers']?.[0];

          const phoneExtension = phone?.Extension;
          const phoneExtensionHTML = !phoneExtension
            ? ''
            : html`<p class="text-right md:text-left">
                ext: ${phoneExtension}
              </p>`;

          const phoneNumber = phone?.Number;
          const phoneHTML = !phoneNumber
            ? ''
            : html`<div
                class="flex justify-between text-body-regular md:justify-start"
              >
                <p class="font-bold md:mr-2">Phone:</p>
                <div>
                  <a
                    class="block font-bold text-pacific-blue decoration-2 hover:underline"
                    href="tel:${phoneNumber}"
                    >${phoneNumber}</a
                  >
                  ${phoneExtensionHTML}
                </div>
              </div>`;

          const faxNumber = hit['FaxNumber']?.Number;
          const faxNumberHTML = !faxNumber
            ? ''
            : html`<div
                class="flex justify-between text-body-regular md:justify-start"
              >
                <p class="font-bold md:mr-2">Fax:</p>
                <a
                  class="block font-bold text-pacific-blue decoration-2 hover:underline"
                  href="tel:${faxNumber}"
                  >${faxNumber}</a
                >
              </div>`;

          const contactSection =
            phoneHTML === '' && faxNumberHTML === ''
              ? ''
              : html`<div class="mt-6 md:mt-0 md:flex-1">
                  <h4 class="text-sm uppercase mb-4 font-semibold">Contact</h4>
                  <div class="space-y-4">${phoneHTML} ${faxNumberHTML}</div>
                </div>`;

          const objectID = hit['objectID'];

          return html`
            ${distanceTab}
            <div class="w-full p-5 lg:py-6 lg:px-8">
              <div class="md:flex">
                <div class="mb-6 md:mb-0 md:mr-5 md:w-1/4 lg:w-52">
                  <div class="rounded-sm overflow-hidden aspect-w-3 aspect-h-2">
                    <a href="${hit['Url']}">
                      <img
                        class="scale-img object-cover w-full h-full"
                        src="${locationImage}"
                        alt="${hit['Title']}"
                        id="img-${objectID}"
                        onMouseEnter=${() => imageHoverOn(objectID)}
                        onMouseLeave=${() => imageHoverOff(objectID)}
                      />
                    </a>
                  </div>
                </div>
                <div class="w-full flex-1">
                  <span
                    class="text-sm uppercase text-gray-800/80 mb-1.5 font-semibold block"
                  >
                    ${hit['ContentType']}
                  </span>
                  <h3 class="mt-0">
                    <a
                      class="btn-link"
                      href="${hit['Url']}"
                      id="link-${hit['objectID']}"
                      onMouseEnter=${() => imageHoverOn(objectID)}
                      onMouseLeave=${() => imageHoverOff(objectID)}
                    >
                      ${components.Highlight({
                        attribute: 'Title',
                        hit,
                      })}
                      ${hit['Address']?.Town
                        ? ` - ${hit['Address']?.Town}`
                        : ''}
                    </a>
                  </h3>
                  ${parentHospitalDiv}
                  <div class="gap-5 w-full mt-6 md:mt-8 md:flex">
                    <div class="md:flex-1">
                      <h4 class="text-sm uppercase mb-4 font-semibold">
                        Address
                      </h4>
                      <p class="text-body-regular">
                        ${buildingName}
                        <span>${hit['Address']?.Line1}<br /></span>
                        ${lineTwo}
                        <span>${hit['Address']?.City}, </span>
                        <span>${hit['Address']?.State} </span>
                        <span>${hit['Address']?.PostalCode}</span>
                      </p>
                      <a
                        class="flex text-body-regular font-bold text-pacific-blue decoration-2 mt-4 hover:underline"
                        href="${hit['GoogleMapsURL']}"
                      >
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          class="w-6 h-6 mr-2"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <g clip-path="url(#clip0_5163_25011)">
                            <path
                              class="fill-current"
                              d="M21.418 10.677L13.289 2.54805C12.9233 2.18227 12.4445 1.99961 11.9652 2C11.5195 2.00153 11.043 2.18422 10.6758 2.54922L2.54803 10.677C2.18264 11.0441 1.99999 11.5207 2 11.966C2.00001 12.4449 2.18266 12.9238 2.54805 13.2891L10.677 21.418C11.043 21.8176 11.5195 21.966 12 21.966C12.4789 21.966 12.9578 21.7833 13.323 21.418L21.4519 13.2891C21.8172 12.9237 22 12.4449 22 11.966C21.9648 11.5207 21.8164 11.0441 21.418 10.677ZM18.0351 11.8449L14.2851 15.1262C13.8637 15.499 13.2488 15.1645 13.2488 14.6563V12.6262H10.7148L10.7148 14.5012C10.7148 15.1887 10.1875 15.7512 9.46483 15.7512C8.74217 15.7512 8.21483 15.1887 8.21483 14.5012V11.3762C8.21483 10.6887 8.77733 10.1262 9.46483 10.1262H13.1797V8.09492C13.1797 7.55391 13.8199 7.275 14.216 7.625L17.966 10.9063C18.1719 11.0246 18.2148 11.1965 18.2148 11.3762C18.2148 11.5559 18.1719 11.7277 18.0351 11.8449Z"
                            />
                          </g>
                          <defs>
                            <clipPath id="clip0_5163_25011">
                              <rect width="24" height="24" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>

                        <span
                          >Get Directions
                          <span class="sr-only"> to ${hit['Title']}</span></span
                        ></a
                      >
                    </div>
                    ${contactSection}
                  </div>
                </div>
              </div>
              <div class="mt-6 border-t flex justify-center md:justify-end">
                <a
                  class="append-icon text-body-regular font-bold text-pacific-blue decoration-2 mt-6 hover:underline md:mt-4"
                  href="${hit['Url']}"
                  ><span>Location Details</span
                  ><svg
                    class="w-4 h-4 fill-current"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 448 512"
                  >
                    <path
                      d="M264.6 70.63l176 168c4.75 4.531 7.438 10.81 7.438 17.38s-2.688 12.84-7.438 17.38l-176 168c-9.594 9.125-24.78 8.781-33.94-.8125c-9.156-9.5-8.812-24.75 .8125-33.94l132.7-126.6H24.01c-13.25 0-24.01-10.76-24.01-24.01s10.76-23.99 24.01-23.99h340.1l-132.7-126.6C221.8 96.23 221.5 80.98 230.6 71.45C239.8 61.85 254.1 61.51 264.6 70.63z"
                    />
                  </svg>
                </a>
              </div>
            </div>
          `;
        },
      },
    }),

    stats({
      container: '[data-app-mobile-results-button]',
      templates: {
        text(data, {}) {
          let count = '';

          if (data.hasManyResults) {
            count += `View ${data.nbHits} Results`;
          } else if (data.hasOneResult) {
            count += `View 1 Result`;
          } else {
            count += `No Results Found`;
          }
          return count;
        },
      },
    }),

    stats({
      container: '[data-app-search-stats]',
      cssClasses: {
        text: 'stats-text',
      },
      templates: {
        text(data, { html }) {
          let count = '';

          const sortByContainer = document.querySelector('.sort-by-container');

          if (data.hasNoResults) {
            sortByContainer?.classList.add('hidden');
          } else {
            sortByContainer?.classList.remove('hidden');
          }

          if (search.status === 'error') {
            return html`
              <div>
                <p class="text-body-regular">Search is offline</p>
                <p>
                  Sorry, it appears our search tool is currently offline or
                  being worked on. The issue should be resolved soon.
                </p>
              </div>
            `;
          } else if (data.hasManyResults) {
            count += `${data.nbHits} Locations`;
          } else if (data.hasOneResult) {
            count += `1 Location`;
          } else {
            count += `Sorry, we couldn't find any results`;
            return html`
              <p class="text-body-regular">
                <strong>${count} for: "${data.query}"</strong>
              </p>
              <p class="text-body-lg mt-10">
                Try rephrasing your search, or try browsing our
                <span> </span>
                <a href="/conditions-and-treatments">condition categories</a>
                <span> </span>
                or
                <span> </span>
                <a href="/people">primary care types</a>
                <span> </span>
                to find what you need.
              </p>
            `;
          }

          if (data.query) {
            return html` <p>
              <strong>${count}</strong> for
              <strong> "${data.query}"</strong> sorted by
              <strong> ${sortingMethod}</strong>
            </p>`;
          } else {
            return html` <p>
              <strong>${count}</strong> sorted by
              <strong> ${sortingMethod}</strong>
            </p>`;
          }
        },
      },
    }),

    customSortBy({
      container: document.querySelector(
        '[data-app-search-refinement="sort-by"]'
      ),
      items: [
        { label: 'A-Z', value: `${searchIndexName.alphabetical}` },
        {
          label: 'Distance',
          value: `${searchIndexName.distance}`,
        },
      ],
    }),

    refinementList({
      attribute: 'ContentType',
      container: '[data-app-search-refinement="location-types"]',
      sortBy: ['name:asc'],
      searchable: false,
      limit: 200,
      cssClasses: {
        list: ['list-none space-y-4'],
        searchableRoot: ['searchableRoot'],
        searchBox: ['refinement-searchbox'],
        searchableInput: ['refinement-input'],
        searchableSubmitIcon: [''],
        checkbox: ['refinement-checkbox'],
      },
      transformItems: (items) => {
        // If Algolia doesn't return enough results, we lose track of a
        // potentially refined facet.
        // For example, if you refine on "Apple", then search for "chromecast",
        // "Apple" is no longer returned, and we don't know that it was selected
        // based on the initial facets.
        // We need to keep track of the last state to reflect the fact that it
        // was refined in the UI.
        initialLocationTypes.forEach((facet, index) => {
          const updatedItem = items.find((item) => item.value === facet.value);

          if (updatedItem) {
            initialLocationTypes[index] = {
              ...updatedItem,
              count: 0,
            };
          }
        });

        return uniqBy([...items, ...initialLocationTypes], 'value')
          .slice(0, 200)
          .sort((a, b) => a.value.localeCompare(b.value));
      },
      templates: {
        item({ label, count }) {
          const canBeRefined = count > 0 ? '' : 'disabled disabled-checkbox';
          const cursor =
            count > 0
              ? 'cursor-pointer'
              : 'cursor-not-allowed text-gray-500/80';

          return `
          <label class="ais-RefinementList-label text-base ${cursor}" >
            <input type="checkbox" class="ais-RefinementList-checkbox refinement-checkbox" ${canBeRefined} value="${label}"/>
              ${label} (${count})
          </label>`;
        },
        searchableNoResults(data, { html }) {
          return html`<span>No results</span>`;
        },
      },
    }),

    refinementList({
      attribute: 'RelatedConditions',
      container: '[data-app-search-refinement="conditions"]',
      sortBy: ['name:asc'],
      searchable: true,
      showMore: true,
      showMoreLimit: 200,
      cssClasses: {
        list: ['list-none space-y-4'],
        searchableRoot: ['searchableRoot'],
        searchBox: ['refinement-searchbox'],
        searchableInput: ['refinement-input'],
        searchableSubmitIcon: [''],
        checkbox: ['refinement-checkbox'],
        showMore: ['show-more-btn mt-4'],
      },
      searchablePlaceholder: '',
      templates: {
        item({ label, count, highlighted }) {
          return `
          <label class="ais-RefinementList-label text-base">
            <input type="checkbox" class="ais-RefinementList-checkbox refinement-checkbox" value="${label}"/>
              <p>${highlighted} (${count})</p>
          </label>`;
        },
        searchableNoResults(data, { html }) {
          return html`<span>No results</span>`;
        },
      },
    }),

    refinementList({
      attribute: 'RelatedDepartments',
      container: '[data-app-search-refinement="departments"]',
      sortBy: ['name:asc'],
      searchable: true,
      showMore: true,
      cssClasses: {
        list: ['list-none space-y-4'],
        searchableRoot: ['searchableRoot'],
        searchBox: ['refinement-searchbox'],
        searchableInput: ['refinement-input'],
        searchableSubmitIcon: [''],
        checkbox: ['refinement-checkbox'],
        showMore: ['show-more-btn mt-4'],
      },
      searchablePlaceholder: '',
      templates: {
        item({ label, count, highlighted }) {
          return `
          <label class="ais-RefinementList-label text-base">
            <input type="checkbox" class="ais-RefinementList-checkbox refinement-checkbox" value="${label}"/>
              <p>${highlighted} (${count})</p>
          </label>`;
        },
        searchableNoResults(data, { html }) {
          return html`<span>No results</span>`;
        },
      },
    }),

    customCurrentRefinements({
      container: document.querySelector('[data-app-search-custom-refinements]'),
    }),

    customGeoSearch({
      container: document.querySelector(
        '[data-app-search-refinement="distance"]'
      ),
    }),

    pagination({
      container: '[data-app-search-pagination]',
      scrollTo: '[data-app-search-hits]',
      showFirst: true,
      showLast: true,
      padding: 2,
      cssClasses: {
        list: 'justify-center remove-space list-none custom-pagination border-t',
        item: 'w-8 h-8 flex items-center justify-center overflow-hidden',
      },
    }),
  ]);

  search.start();
}
